import { IResourceComponentsProps, useTranslate, useShow } from '@pankod/refine-core';
import {
  Show,
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Checkbox,
} from '@pankod/refine-antd';
import 'react-mde/lib/styles/css/react-mde-all.css';
import { User } from 'interfaces';
import { UserRoleEnum } from 'types/users/userRole';
import { EnumToArray } from 'util/parseData';
import { useEffect } from 'react';

export const UserShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<User>();
  const { data, isLoading } = queryResult;
  const record = data?.data;
  const [form] = Form.useForm();

  const t = useTranslate();

  useEffect(() => {
    form?.setFieldsValue(record);
  }, []);

  return (
    <Show isLoading={isLoading}>
      <style>
        {`
          .user-show-container {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
            padding: 24px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            transition: box-shadow 0.2s ease;
          }
          .user-show-container:hover {
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
          }
          .user-show-container .ant-form-item {
            margin-bottom: 16px;
          }
          .user-show-container .ant-form-item-label > label {
            font-size: 12px;
            font-weight: 600;
            color: #1f2a44;
            padding-bottom: 4px;
            border-bottom: 1px solid #2563eb;
            display: inline-block;
            text-transform: capitalize;
          }
          .user-show-container .ant-input,
          .user-show-container .ant-select-selector,
          .user-show-container .ant-input-number {
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            padding: 8px 12px;
            font-size: 14px;
            color: #1f2a44;
            background: #ffffff;
            transition: all 0.2s ease;
          }
          .user-show-container .ant-input:hover,
          .user-show-container .ant-select-selector:hover,
          .user-show-container .ant-input-number:hover {
            border-color: #3b82f6;
            background: #f8f9fa;
          }
          .user-show-container .ant-input:focus,
          .user-show-container .ant-select-selector:focus,
          .user-show-container .ant-input-number:focus {
            border-color: #2563eb;
            box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
            background: #ffffff;
          }
          .user-show-container .ant-input[disabled],
          .user-show-container .ant-select-disabled .ant-select-selector,
          .user-show-container .ant-input-number-disabled {
            background: #f5f5f5;
            color: #595959;
            border-color: #d9d9d9;
            cursor: not-allowed;
          }
          .user-show-container .ant-select-arrow {
            color: #1f2a44;
          }
          .user-show-container .ant-checkbox-wrapper {
            font-size: 14px;
            color: #1f2a44;
            padding: 6px 10px;
            border-radius: 4px;
            display: inline-flex;
            align-items: center;
            transition: background 0.2s ease;
          }
          .user-show-container .ant-checkbox-wrapper:hover {
            background: #f8f9fa;
          }
          .user-show-container .ant-checkbox-inner {
            width: 16px;
            height: 16px;
            border: 1px solid #d9d9d9;
            border-radius: 3px;
            background: #ffffff;
          }
          .user-show-container .ant-checkbox:hover .ant-checkbox-inner,
          .user-show-container .ant-checkbox-input:focus + .ant-checkbox-inner {
            border-color: #3b82f6;
          }
          .user-show-container .ant-checkbox-checked .ant-checkbox-inner {
            background: #2563eb;
            border-color: #2563eb;
          }
          .user-show-container .ant-checkbox-checked::after {
            border-color: #3b82f6;
          }
          .user-show-container .ant-col {
            padding: 12px;
          }
          @media (max-width: 768px) {
            .user-show-container {
              padding: 16px;
            }
            .user-show-container .ant-form-item-label > label {
              font-size: 11px;
            }
            .user-show-container .ant-input,
            .user-show-container .ant-select-selector,
            .user-show-container .ant-input-number {
              font-size: 13px;
              padding: 6px 10px;
            }
            .user-show-container .ant-checkbox-wrapper {
              font-size: 13px;
              padding: 4px 8px;
            }
            .user-show-container .ant-checkbox-inner {
              width: 14px;
              height: 14px;
            }
            .user-show-container .ant-col {
              padding: 8px;
            }
          }
          @media (max-width: 576px) {
            .user-show-container {
              border-radius: 6px;
              padding: 12px;
            }
            .user-show-container .ant-form-item {
              margin-bottom: 12px;
            }
            .user-show-container .ant-form-item-label > label {
              font-size: 10px;
            }
            .user-show-container .ant-input,
            .user-show-container .ant-select-selector,
            .user-show-container .ant-input-number {
              font-size: 12px;
              padding: 5px 8px;
            }
            .user-show-container .ant-checkbox-wrapper {
              font-size: 12px;
              padding: 3px 6px;
            }
            .user-show-container .ant-col {
              padding: 6px;
            }
          }
        `}
      </style>
      <div className="user-show-container">
        <Form
          form={form}
          initialValues={record}
          name="time_related_controls"
          layout="vertical"
        >
          <Form.Item hidden name="table">
            <Input disabled type="hidden" />
          </Form.Item>
          <Row>
            <Col className="gutter-row" span={24}>
              <Form.Item name="newsletter" valuePropName="checked" noStyle>
                <Checkbox>{t('fields.newsletter', { ns: 'users' })}</Checkbox>
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
              <Form.Item
                label={t('fields.lastname', { ns: 'users' })}
                name="lastname"
                rules={[{ required: true, message: 'Please input lastname' }]}
              >
                <Input disabled maxLength={255} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
              <Form.Item
                label={t('fields.firstname', { ns: 'users' })}
                name="firstname"
                rules={[{ required: true, message: 'Please input firstname' }]}
              >
                <Input disabled maxLength={255} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={8}>
              <Form.Item
                label={t('fields.role', { ns: 'users' })}
                name="role"
                rules={[{ required: true }]}
              >
                <Select disabled options={EnumToArray(UserRoleEnum)} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
              <Form.Item
                label={t('fields.email', { ns: 'users' })}
                name="email"
                rules={[
                  { type: 'email', message: 'The input is not valid E-mail!' },
                  { required: true, message: 'Please input email' },
                ]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
              <Form.Item
                label={t('fields.phone', { ns: 'users' })}
                name="phone"
                rules={[{ required: true, message: 'Please input phone' }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={24} md={24} lg={8} xl={8}>
              <Form.Item
                label={t('fields.language', { ns: 'users' })}
                name="language"
                rules={[{ required: true, message: 'Please input language' }]}
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={12} md={12} lg={8} xl={8}>
              <Form.Item
                label={t('fields.parent_user_id', { ns: 'users' })}
                name="parent_user_id"
              >
                <InputNumber disabled min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={12} md={12} lg={8} xl={8}>
              <Form.Item
                label={t('fields.total_votes', { ns: 'users' })}
                name="total_votes"
              >
                <InputNumber disabled min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col className="gutter-row" xs={24} sm={12} md={12} lg={8} xl={8}>
              <Form.Item
                label={t('fields.ok_votes', { ns: 'users' })}
                name="ok_votes"
              >
                <InputNumber disabled min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </Show>
  );
};